import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import ClientFields, { EMPTY_CLIENT, clientError } from './ClientFields';
import { searchKey } from '../lib/search';

// Dva načina unosa kupca na fakturi:
// 1) Autocomplete po nazivu (postojeći kupci iz baze)
// 2) Ručni unos svih podataka
export default function ClientPicker({ value, onChange }) {
  const { activeAgencyId } = useAgency();
  const { t, tr } = useI18n();
  const [mode, setMode] = useState('search'); // search | manual
  const [query, setQuery] = useState(value?.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [manual, setManual] = useState(EMPTY_CLIENT);
  const [touched, setTouched] = useState({ pib: false, mb: false });

  useEffect(() => {
    if (mode !== 'search' || !query || query.length < 2 || !activeAgencyId) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // Pretraga u aplikaciji (ne u bazi), da "teatar" nađe i "ТЕАТАР" i obrnuto
      const { data } = await supabase
        .from('customers')
        .select('*')
        .eq('agency_id', activeAgencyId)
        .order('name')
        .limit(1000);
      const q = searchKey(query.trim());
      setSuggestions((data || []).filter((c) => searchKey(`${c.name} ${c.pib || ''}`).includes(q)).slice(0, 6));
    }, 250);
  }, [query, mode, activeAgencyId]);

  const selectExisting = (customer) => {
    onChange(customer);
    setQuery(customer.name);
    setShowSuggestions(false);
  };

  const saveManualAndSelect = async () => {
    setSaveError('');
    const vErr = clientError(manual, tr);
    if (vErr) { setTouched({ pib: true, mb: true }); setSaveError(vErr); return; }
    if (!activeAgencyId) {
      setSaveError('Nije izabrana agencija.');
      return;
    }
    setSaving(true);
    const insert = (row) => supabase.from('customers').insert({ ...row, agency_id: activeAgencyId }).select().single();
    let { data, error } = await insert(manual);
    // Ako u bazi još nema kolone "is_individual", sačuvaj bez nje
    if (error && /is_individual/.test(error.message || '')) {
      const { is_individual, ...rest } = manual; // eslint-disable-line no-unused-vars
      ({ data, error } = await insert(rest));
    }
    setSaving(false);
    if (error) {
      // Prikaži tačnu poruku baze da bi se video uzrok
      console.error('Greška pri čuvanju kupca:', error);
      setSaveError(error.message + (error.details ? ' — ' + error.details : '') + (error.hint ? ' (' + error.hint + ')' : ''));
      return;
    }
    if (data) {
      onChange(data);
      setQuery(data.name);
      setMode('search');
      setShowSuggestions(false);
    }
  };

  if (mode === 'manual') {
    return (
      <div style={styles.box}>
        <div style={styles.modeHeader}>
          <span style={styles.modeTitle}>{t('newManualCustomer')}</span>
          <button type="button" onClick={() => setMode('search')} style={styles.modeBack}>{t('backToSearch')}</button>
        </div>
        <ClientFields form={manual} setForm={setManual} touched={touched} setTouched={setTouched} compact />
        {saveError && <div style={styles.errorBox}>{saveError}</div>}
        <button type="button" onClick={saveManualAndSelect} disabled={saving} style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }}>
          {saving ? t('savingEllipsisShort') : t('saveCustomerBtn')}
        </button>
      </div>
    );
  }

  // search mode
  return (
    <div style={{ position: 'relative' }}>
      <input
        placeholder={t('typeCustomerName')}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); onChange(null); }}
        onFocus={() => setShowSuggestions(true)}
        style={styles.input}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div style={styles.suggestBox}>
          {suggestions.map((c) => (
            <button key={c.id} type="button" onClick={() => selectExisting(c)} style={styles.suggestItem}>
              <span style={styles.suggestName}>{c.name}</span>
              {c.pib && <span style={styles.suggestSub}>{t('pibShort')}: {c.pib}</span>}
            </button>
          ))}
        </div>
      )}
      <div style={styles.altRow}>
        <button type="button" onClick={() => setMode('manual')} style={styles.altBtn}>{t('newManualBtn')}</button>
      </div>
    </div>
  );
}

const styles = {
  box: { border: '1.5px solid var(--line)', borderRadius: 10, padding: 14, background: 'var(--lavender-100)' },
  modeHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  modeTitle: { fontSize: 13, fontWeight: 700, color: 'var(--teal-800)' },
  modeBack: { border: 'none', background: 'none', color: 'var(--teal-700)', fontSize: 12, fontWeight: 600, padding: 0 },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 14, width: '100%', marginBottom: 8 },
  inputBad: { borderColor: 'var(--red-600)', background: 'var(--red-100)' },
  fieldError: { fontSize: 12, color: 'var(--red-600)', marginTop: -4, marginBottom: 8, lineHeight: 1.4 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-700)', margin: '4px 0 10px' },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12, marginBottom: 10, wordBreak: 'break-word' },
  saveBtn: { width: '100%', minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
  suggestBox: {
    position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)',
    border: '1px solid var(--line)', borderRadius: 8, boxShadow: 'var(--shadow-md)', zIndex: 10,
    marginTop: 4, overflow: 'hidden',
  },
  suggestItem: {
    display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left', padding: '10px 14px',
    border: 'none', background: 'none', borderBottom: '1px solid var(--line)',
  },
  suggestName: { fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' },
  suggestSub: { fontSize: 12, color: 'var(--ink-500)' },
  altRow: { display: 'flex', gap: 14, marginTop: 4, flexWrap: 'wrap' },
  altBtn: { border: 'none', background: 'none', color: 'var(--teal-700)', fontSize: 12, fontWeight: 600, padding: 0 },
};
